import { Grid } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BaseButton } from 'src/common/components/base-button';
import { SizeType } from 'src/common/enumerations/cashier-preference-level';
import { groupBy } from 'src/common/helpers/array-helper';
import { calculateHeight, calculateWidth } from 'src/common/helpers/dimension-helper';
import { CashierPreferences } from 'src/common/interfaces/dtos/CashierPreferences';
import { ISidebarButton } from 'src/common/interfaces/dtos/ISidebarButton';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { AppState } from 'src/common/redux/configure-store';
import { useResponsiveContext } from 'src/home-page/hooks/responsive-context';
import { ReturnsType } from 'src/transaction-search/interfaces/get-effective-return-policy-response';
import { isButtonDisabled } from '../../business-logic/button-disabled';
import { Commands } from '../commands';
import SidebarButton from './sidebar-button';
import { styles } from './sidebar-styles';

const getNameFromValue = (key: number): string => {
  return (
    {
      0: 'General',
      1: 'Products',
      2: 'Promotions',
      3: 'Cash Office',
      4: 'Recall Order',
    }[key] || 'Products'
  );
};

const Sidebar = ({
  isTransactionActive,
  hasCustomer,
  isSidebarDisabled,
  hasCashDrawer,
  hasPinpad,
  selectedItem,
  hasProducts,
  hasCoupons,
  middleAreaTabValue,
  isCollapsed,
  buttons,
  trainingModeActive,
  saleModeFromReturnMode,
  isTenderModeActivate,
}: ISidebarProps): JSX.Element => {
  const buttonSize = useSelector<AppState, SizeType>(
    state => state.cashierPreferences.buttonZoomLevel,
  );
  const isTBRActive = useSelector(
    (state: AppState): boolean =>
      state.posMode.returnMode.returnType === ReturnsType.TransactionBasedReturn,
  );
  const isInquiryProcessing = useSelector(
    (state: AppState): boolean => state.balanceInquiry.isProcessing,
  );

  const ownBagEnabled = useSelector(
    (state: AppState): boolean =>
      state.touchPoint.touchPointConfiguration.ownBagConfiguration?.ownBagEnabled || false,
  );

  const isProcessingActive = isInquiryProcessing;
  const buttonEl = useRef<HTMLDivElement>(null);
  const classes = styles();
  const { t } = useTranslation();
  const { showSidebar } = useResponsiveContext();
  const [commandsVisible, setCommandsVisible] = useState<boolean>(false);
  const [buttonCount, setButtonCount] = useState<number>(9);
  const [commandsCount, setCommandsCount] = useState<number>(9);
  const [currentButtons, setCurrentButtons] = useState<ISidebarButton[]>(buttons);
  const [isSubCategory, setIsSubCategory] = useState<boolean>(false);
  const [groupName, setGroupName] = useState<string>();

  const closeAndReset = useCallback(() => {
    setCommandsVisible(false);
    setCurrentButtons(buttons);
    setIsSubCategory(false);
  }, [buttons]);

  const handleGroupClick = useCallback(
    (groupName: string, buttonGroup: ISidebarButton[]) => {
      if (!commandsVisible) {
        setCommandsVisible(true);
      }
      setIsSubCategory(true);
      setCurrentButtons(buttonGroup);
      setGroupName(groupName);
    },
    [commandsVisible],
  );

  /* the tabName is either General, Products, Promotions, Cash Office, or Recall Order */
  const tabName = getNameFromValue(middleAreaTabValue);
  const isDisabled = isButtonDisabled({
    isTransactionActive,
    hasCustomer,
    disableButton: isSidebarDisabled,
    hasCashDrawer,
    hasPinpad,
    selectedItem,
    tabName,
    hasProducts,
    hasCoupons,
    saleModeFromReturnMode,
    isTBRActive,
    isProcessingActive,
    isTenderModeActivate,
    ownBagEnabled,
  });

  const buttonContainerRef = useRef<HTMLDivElement>(null);

  const handleResize = useCallback((): void => {
    let count = 0;
    let commandsCount = 0;

    // TODO: Possibly move this out to a helper somewhere
    const buttonHeight = buttonEl?.current?.getBoundingClientRect()?.height || 56;
    const buttonMargin = (calculateHeight(10).dimension / 100) * window.innerHeight;
    const buttonTotal = buttonHeight + buttonMargin;
    // Window height minus 2 buttons (Menu and More) minus 4 button margins (top and bottom of Menu and More)
    const containerHeight = window.innerHeight - 2 * buttonHeight - 4 * buttonMargin;
    count = Math.floor(containerHeight / buttonTotal);
    commandsCount = Math.floor(containerHeight / buttonTotal);
    if (!showSidebar) {
      const buttonWidth = buttonEl?.current?.getBoundingClientRect()?.width || 114;
      const buttonMargin = (calculateWidth(10).dimension / 100) * window.innerWidth;
      const buttonTotal = buttonWidth + buttonMargin;
      const containerWidth = window.innerWidth - 4 * buttonWidth - buttonMargin;
      count = Math.floor(containerWidth / buttonTotal);
    }

    setButtonCount(count);
    setCommandsCount(commandsCount);
  }, [showSidebar, setButtonCount, setCommandsCount]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    if (!isSubCategory) {
      setCurrentButtons(buttons);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, [buttonSize, buttons, isSubCategory, handleResize]);

  const renderButtons = (buttons: ISidebarButton[]): JSX.Element[] =>
    buttons.slice(0, buttonCount).map(
      (button: ISidebarButton, index: number): JSX.Element => (
        <div
          key={index}
          style={{ marginBottom: `${showSidebar ? calculateHeight(10).stringValue : '0'}` }}
        >
          <SidebarButton
            text={button.text}
            handleClick={(): void =>
              button.buttonGroup
                ? handleGroupClick(button.text, button.buttonGroup)
                : button.handleClick()
            }
            isCollapsed={isCollapsed}
            buttonIcon={button.buttonIcon}
            isSelected={button.isSelected ?? false}
            isDisabled={button.isDisabled ? button.isDisabled : isDisabled(button.appliesTo)}
            className={button.className}
            appliesTo={button.appliesTo}
            buttonType='alternate'
            buttonGroup={button.buttonGroup}
            showIcon={showSidebar}
          />
        </div>
      ),
    );

  const renderGridButtons = (): JSX.Element => {
    const buttonGroups = groupBy(buttons, 'category');
    return (
      <Grid
        container
        direction='column'
        justifyContent='center'
        alignItems='center'
        className='fullWidth'
      >
        <Grid
          item
          className={clsx('fullWidth', {
            [!showSidebar
              ? classes.buttonsContainerSmallForm
              : classes.buttonsContainer]: !showSidebar,
          })}
        >
          {Object.keys(buttonGroups).length === 1
            ? renderButtons(buttons)
            : Object.keys(buttonGroups).map(
                (key: string): JSX.Element => (
                  <React.Fragment key={key}>
                    <h5>{key.toUpperCase()}</h5>
                    {renderButtons(buttonGroups[key])}
                  </React.Fragment>
                ),
              )}
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid
      container
      direction={showSidebar ? 'column' : 'row-reverse'}
      className='fullHeight'
      wrap='nowrap'
    >
      <Grid item ref={buttonContainerRef} className={`fullWidth ${classes.actionButtonsContainer}`}>
        <Grid
          container
          direction='column'
          alignItems='flex-start'
          justifyContent='space-between'
          className={`fullHeight`}
        >
          {renderGridButtons()}
        </Grid>
      </Grid>
      <Grid
        item
        ref={buttonEl}
        className={clsx(classes.moreButtonContainer, {
          [classes.moreButtonContainerMargin]: showSidebar,
        })}
      >
        <BaseButton
          data-testid={'sidebar-button-more'}
          onClick={(): void => setCommandsVisible(true)}
          disabled={buttons.length <= buttonCount || isSidebarDisabled}
          startIcon={<MoreVertIcon />}
          buttonType='alternate'
          classes={{
            root: !showSidebar ? classes.moreButtonSmallForm : classes.moreButton,
            startIcon: classes.moreIcon,
            label: classes.moreLabel,
          }}
        >
          {t('MoreLabel')}
        </BaseButton>
      </Grid>
      <Commands
        visible={commandsVisible}
        buttons={currentButtons}
        isDisabled={isDisabled}
        closeAndReset={closeAndReset}
        trainingModeActive={trainingModeActive}
        buttonCount={commandsCount}
        handleGroupClick={handleGroupClick}
        isSubCategory={isSubCategory}
        subGroupName={groupName}
      />
    </Grid>
  );
};
export interface ISidebarProps {
  isCollapsed: boolean;
  buttons: ISidebarButton[];
  selectedItem?: IElectronicJournalRecord;
  isTransactionActive: boolean;
  middleAreaTabValue: number;
  hasCustomer: boolean;
  isSidebarDisabled: boolean;
  hasCashDrawer: boolean;
  hasPinpad: boolean;
  hasProducts: boolean;
  hasCoupons: boolean;
  trainingModeActive: boolean;
  saleModeFromReturnMode: boolean;
  cashierPreferences: CashierPreferences;
  isTenderModeActivate: boolean;
}
export default Sidebar;
